import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { Limits, Sorting } from '../types/sort';

const DEFAULT_PAGE_SIZE = 20;

export abstract class Store<TItem, TFilter, TSorting extends Sorting<string>> {
    protected _loading = false;

    protected _size = 0;
    private _page = 0;
    private _pageSize = DEFAULT_PAGE_SIZE;
    protected _items: TItem[] = [];
    private _filter: TFilter;
    private _sorting: TSorting;

    private readonly _defaultFilter: TFilter;
    private readonly _defaultSorting: TSorting;

    constructor(filter: TFilter, sorting: TSorting, defaultPageSize = DEFAULT_PAGE_SIZE) {
        this._pageSize = defaultPageSize;
        this._filter = filter;
        this._defaultFilter = filter;
        this._sorting = sorting;
        this._defaultSorting = sorting;
        // makeAutoObservable(this);
        makeObservable<
            Store<TItem, TFilter, TSorting>,
            '_loading' | '_size' | '_page' | '_pageSize' | '_items' | '_filter' | '_sorting'
        >(this, {
            _loading: observable,
            _size: observable,
            _page: observable,
            _pageSize: observable,
            _items: observable,
            _filter: observable,
            _sorting: observable,
            loading: computed,
            size: computed,
            page: computed,
            pageSize: computed,
            items: computed,
            filter: computed,
            sorting: computed,
            setPage: action,
            setPageSize: action,
            setFilter: action,
            setSorting: action,
            loadSize: action,
            loadItems: action,
            load: action,
            reset: action,
        });
    }

    get loading(): boolean {
        return this._loading;
    }

    //-------------------------------------------------------------------------------------------------
    get size(): number {
        return this._size;
    }

    //-------------------------------------------------------------------------------------------------
    get page(): number {
        return this._page;
    }

    //-------------------------------------------------------------------------------------------------
    get pageSize(): number {
        return this._pageSize;
    }

    //-------------------------------------------------------------------------------------------------
    get items(): TItem[] {
        return this._items;
    }

    //-------------------------------------------------------------------------------------------------
    get filter(): TFilter {
        return this._filter;
    }

    //-------------------------------------------------------------------------------------------------
    get sorting(): TSorting {
        return this._sorting;
    }

    //=================================================================================================
    protected get limit(): Limits {
        return { offset: this.page * this.pageSize, limit: this.pageSize };
    }

    async setPage(page: number): Promise<void> {
        runInAction(() => {
            this._page = page;
        });
        await this.loadItems();
    }

    //-------------------------------------------------------------------------------------------------
    async setPageSize(pageSize: number): Promise<void> {
        runInAction(() => {
            this._pageSize = pageSize;
        });
        await this.loadItems();
    }

    //-------------------------------------------------------------------------------------------------
    async setFilter(filter: TFilter): Promise<void> {
        runInAction(() => {
            this._filter = filter;
        });
        await this.load();
    }

    async setSorting(sorting: TSorting): Promise<void> {
        runInAction(() => {
            this._sorting = sorting;
        });
        await this.loadItems();
    }

    //=================================================================================================
    async loadSize(): Promise<number> {
        runInAction(() => {
            this._loading = true;
        });
        try {
            const size = await this.getSize();
            runInAction(() => {
                this._size = size;
            });
            return this.size;
        } catch (e) {
            throw e;
        } finally {
            runInAction(() => {
                this._loading = false;
            });
        }
    }
    async loadItems(): Promise<void> {
        runInAction(() => {
            this._loading = true;
        });
        try {
            const items = await this.getItems();
            runInAction(() => {
                this._items = items;
            });
        } catch (e) {
            throw e;
        } finally {
            runInAction(() => {
                this._loading = false;
            });
        }
    }

    async load(): Promise<void> {
        await this.loadSize();
        await this.loadItems();
    }

    //=================================================================================================
    async reset(): Promise<void> {
        runInAction(() => {
            this._page = 0;
            this._pageSize = DEFAULT_PAGE_SIZE;
            this._filter = this._defaultFilter;
            this._sorting = this._defaultSorting;
        });
        await this.load();
    }

    //=================================================================================================
    protected abstract getSize(): Promise<number>;
    protected abstract getItems(): Promise<TItem[]>;
}
